'use client';

import React from 'react';
import Link from 'next/link';
import {
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Link2,
  QrCode,
  BadgeCheck,
  Building2,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

const STEPS = [
  {
    n: '01',
    title: 'Institution Demo Console',
    desc: 'Institution logs in and accesses the Trust Verification module in the dashboard. Institution-side issuance requires demo account access.',
    href: '/login?next=/institution-dashboard/trust-ledger',
    cta: 'Institution Login',
    color: '#1B3A6B',
    accentBg: 'rgba(27,58,107,0.07)',
    icon: <Building2 size={20} />,
    done: true,
    loginRequired: true,
  },
  {
    n: '02',
    title: 'Sample Credential',
    desc: 'Issuer creates a test supplier-style credential record for Test Supplier A. Credential issuance requires authenticated institution access.',
    href: '/login?next=/institution-dashboard/trust-ledger',
    cta: 'Login to Issue',
    color: '#e9a84c',
    accentBg: 'rgba(233,168,76,0.07)',
    icon: <ShieldCheck size={20} />,
    done: true,
    loginRequired: true,
  },
  {
    n: '03',
    title: 'Public Verification Link',
    desc: 'A shareable public link is generated. Anyone can check credential status without logging in.',
    href: '/trust-ledger/verify?id=ERT-CERT-DEMO-SUPPLY-001',
    cta: 'Open Verify Portal',
    color: '#16a34a',
    accentBg: 'rgba(22,163,74,0.07)',
    icon: <Link2 size={20} />,
    done: true,
  },
  {
    n: '04',
    title: 'Certificate Record',
    desc: 'The full certificate record is publicly viewable, including evidence status and review trail.',
    href: '/certificate/rec-demo-supplier',
    cta: 'View Certificate',
    color: '#7c3aed',
    accentBg: 'rgba(124,58,237,0.07)',
    icon: <BadgeCheck size={20} />,
    done: true,
  },
  {
    n: '05',
    title: 'Public Status Page',
    desc: 'Third party opens the dedicated status page showing current credential state in real time.',
    href: '/trust-ledger/verify',
    cta: 'Open Status Page',
    color: '#0284c7',
    accentBg: 'rgba(2,132,199,0.07)',
    icon: <QrCode size={20} />,
    done: true,
  },
];

const QUICK_LINKS = [
  {
    label: 'Institution Demo Console',
    href: '/institution-dashboard/trust-ledger',
    tag: 'Demo account access',
  },
  {
    label: 'Trust Verification Module',
    href: '/institution-dashboard/trust-ledger',
    tag: 'Demo account access',
  },
  { label: 'Public Verification Portal', href: '/trust-ledger/verify?id=ERT-CERT-DEMO-SUPPLY-001', tag: 'Public' },
  { label: 'Demo Certificate Record', href: '/certificate/rec-demo-supplier', tag: 'Public' },
  { label: 'Public Status Page', href: '/trust-ledger/verify', tag: 'Public' },
  { label: 'Trust Ledger Product Page', href: '/product/trust-ledger', tag: 'Public' },
];

export default function TrustLedgerDemoPage() {
  return (
    <div
      className="min-h-screen bg-white text-slate-950"
      style={{ fontFamily: 'var(--font-dm-sans, sans-serif)' }}
    >
      {/* Header */}
      <div
        className="border-b border-white/10 sticky top-0 z-20"
        style={{ background: 'rgba(10,22,40,0.98)', backdropFilter: 'blur(10px)' }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: '#e9a84c', opacity: 0.65 }}
        />
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-white text-sm">Ertzyx</span>
          </Link>
          <div className="flex items-center gap-3">
            <span
              className="text-xs font-bold px-3 py-1.5 rounded-full"
              style={{
                background: 'rgba(233,168,76,0.12)',
                color: '#e9a84c',
                border: '1px solid rgba(233,168,76,0.3)',
              }}
            >
              Demo Overview
            </span>
            <Link
              href="/login?next=/institution-dashboard/trust-ledger"
              className="text-xs font-bold px-3 py-2 rounded-lg text-white transition-all hover:opacity-90"
              style={{ background: '#e9a84c', color: '#06101e' }}
            >
              Institution Login
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div
        className="px-6 py-16 text-center"
        style={{ background: 'linear-gradient(135deg, #06101e 0%, #0d1f3c 70%, #06101e 100%)' }}
      >
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-5 text-xs font-bold"
          style={{
            borderColor: 'rgba(239,68,68,0.4)',
            background: 'rgba(239,68,68,0.07)',
            color: '#f87171',
          }}
        >
          <AlertTriangle size={12} />
          Pre-commercial demo · Not government-approved · Synthetic test data only
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight">
          Trust Ledger Demo Flow
        </h1>
        <p
          className="text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-2"
          style={{ color: 'rgba(232,237,243,0.7)' }}
        >
          A minimal, truthful demonstration of the Ertzyx Trust Ledger verification workflow — from
          institution credential creation to public third-party status check.
        </p>
        <p
          className="text-sm max-w-xl mx-auto leading-relaxed"
          style={{ color: 'rgba(232,237,243,0.45)' }}
        >
          This flow uses the existing Ertzyx platform foundation. All data is synthetic and clearly
          labeled as demo-only.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Flow Steps */}
        <div className="mb-12">
          <h2 className="text-xl font-black text-slate-900 mb-2">Demonstration flow</h2>
          <p className="text-sm text-slate-500 mb-8">
            Each step is a real, accessible page in the current Ertzyx platform.
          </p>
          <div className="flex flex-col gap-4">
            {STEPS.map((step, i) => (
              <div
                key={step.n}
                className="rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all"
                style={{ background: '#fff' }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-5 p-6">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: step.accentBg,
                      color: step.color,
                      border: `1.5px solid ${step.color}20`,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-mono text-lg font-black" style={{ color: step.color }}>
                        {step.n}
                      </span>
                      <h3 className="font-bold text-slate-900 text-base">{step.title}</h3>
                      {step.done && (
                        <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                      )}
                      {'loginRequired' in step && step.loginRequired && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Demo account access
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                  </div>
                  <Link
                    href={step.href}
                    target={step.href.startsWith('http') ? '_blank' : undefined}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90 flex-shrink-0 text-white"
                    style={{ background: step.color }}
                  >
                    {step.cta}
                    <ExternalLink size={13} />
                  </Link>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex justify-center pb-2">
                    <ArrowRight size={16} className="text-slate-300 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick links panel */}
        <div className="mb-12">
          <h2 className="text-xl font-black text-slate-900 mb-2">Quick links for screenshots</h2>
          <p className="text-sm text-slate-500 mb-6">
            All public links work right now in the browser.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                target={link.tag === 'Public' ? '_blank' : undefined}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-sm transition-all px-4 py-3"
              >
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{link.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{link.tag}</p>
                </div>
                <ExternalLink size={14} className="text-slate-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>

        {/* Capabilities table */}
        <div className="mb-12">
          <h2 className="text-xl font-black text-slate-900 mb-2">
            What this demo truthfully shows
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            Each capability listed is functional in the current platform.
          </p>
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <div
              className="grid grid-cols-3 px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100"
              style={{ background: '#f8f9fb' }}
            >
              <div>Capability</div>
              <div>Current State</div>
              <div>Notes</div>
            </div>
            {[
              {
                cap: 'Institution dashboard login',
                state: 'Live',
                notes: 'Real auth via Supabase',
              },
              {
                cap: 'Trust Verification module',
                state: 'Live',
                notes: 'Full UI at /institution-dashboard/trust-ledger',
              },
              {
                cap: 'Demo credential record',
                state: 'Live',
                notes: 'In publicRecords.ts; visible at /certificate/rec-demo-supplier',
              },
              {
                cap: 'Public verification link',
                state: 'Live',
                notes: '/trust-ledger/verify?id=ERT-CERT-DEMO-SUPPLY-001 works now',
              },
              {
                cap: 'QR code generation',
                state: 'Live',
                notes: 'Via api.qrserver.com — real scannable QR',
              },
              {
                cap: 'Status toggle (demo)',
                state: 'Live UI only',
                notes: 'Frontend-only on demo pages; issuer status update not persisted',
              },
              {
                cap: 'Public status page',
                state: 'Live',
                notes: '/trust-ledger/verify — dedicated clean status page',
              },
              {
                cap: 'Certificate record view',
                state: 'Live',
                notes: '/certificate/rec-demo-supplier — full certificate document',
              },
              {
                cap: 'Revocation workflow',
                state: 'Not implemented',
                notes: 'Architecture planned; no issuer revocation API yet',
              },
              {
                cap: 'Embeddable badge API',
                state: 'Not implemented',
                notes: 'Badge preview exists; embed SDK not built',
              },
              {
                cap: 'External API endpoint',
                state: 'Not implemented',
                notes: 'Machine-readable status endpoint not exposed',
              },
            ].map((row) => (
              <div
                key={row.cap}
                className="grid grid-cols-3 px-5 py-3.5 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50"
              >
                <span className="text-sm font-medium text-slate-800">{row.cap}</span>
                <span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background: row.state.startsWith('Live')
                        ? row.state === 'Live'
                          ? 'rgba(22,163,74,0.1)'
                          : 'rgba(233,168,76,0.1)'
                        : 'rgba(239,68,68,0.08)',
                      color: row.state.startsWith('Live')
                        ? row.state === 'Live'
                          ? '#16a34a'
                          : '#d97706'
                        : '#dc2626',
                    }}
                  >
                    {row.state}
                  </span>
                </span>
                <span className="text-xs text-slate-500 leading-relaxed">{row.notes}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div
          className="rounded-2xl border-2 p-6"
          style={{ borderColor: 'rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.03)' }}
        >
          <div className="flex items-start gap-4">
            <AlertTriangle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-black text-slate-900 text-base mb-2">
                What this demo does not claim
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  'Government of Canada approval or endorsement',
                  'PSPC certification or official procurement status',
                  'Production integration with GC systems',
                  'Cryptographic signing, blockchain, or biometric anchoring',
                  'Immutable or tamper-proof record storage',
                  'Legal credential equivalence',
                ].map((item) => (
                  <p key={item} className="text-sm text-red-700 flex items-start gap-2">
                    <span className="text-red-400 flex-shrink-0 mt-0.5">
                      <AlertTriangle size={12} />
                    </span>
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
